// İletişim formu (/contact) saf yardımcıları — React/MUI/i18n bağımlılığı yok; Node testleri: tests/utils/contactForm.test.mjs.
// Sunucu sözleşmesi: POST api/Contact/Send (ad 2–100, e-posta ≤ 254, konu kodu, mesaj 10–2000; "website" bal küpü boş olmalı;
// formOpenedAt/submittedAt aynı istemci saatiyle → doldurma süresi saat farkından etkilenmez). Kişisel veri hiçbir yere loglanmaz.

/** Konu kodları (sunucu ile aynı; etiketler i18n: contact.form.subject.options.<kod>). */
export const CONTACT_TOPICS = ['general', 'crm', 'property', 'roommate', 'partnership', 'other'];
export const DEFAULT_TOPIC = 'general';

export const NAME_MIN = 2;
export const NAME_MAX = 100;
export const EMAIL_MAX = 254;
export const MESSAGE_MIN = 10;
export const MESSAGE_MAX = 2000;
export const SOURCE_PATH_MAX = 200;

// ?konu= değerleri (Türkçe karakterler katlanır, büyük/küçük harf duyarsız)
const QUERY_TOPICS = {
  general: 'general',
  genel: 'general',
  crm: 'crm',
  'emlak-crm': 'crm',
  kurumsal: 'crm',
  property: 'property',
  'mulk-takibi': 'property',
  mulk: 'property',
  roommate: 'roommate',
  'ev-arkadasi': 'roommate',
  ilan: 'roommate',
  ilanlar: 'roommate',
  partnership: 'partnership',
  'is-birligi': 'partnership',
  isbirligi: 'partnership',
  basin: 'partnership',
  other: 'other',
  diger: 'other',
};

const TR_FOLD = { ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u', â: 'a', î: 'i', û: 'u' };

/** "Mülk_Takibi " → "mulk-takibi" */
export const foldSlug = (value) =>
  String(value ?? '')
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/i̇/g, 'i')
    .replace(/[çğıöşüâîû]/g, (ch) => TR_FOLD[ch] || ch)
    .replace(/[\s_]+/g, '-');

/** ?konu= (URLSearchParams, "?konu=crm" ya da "konu=crm") → konu kodu; bilinmeyen/boş → general. */
export const topicFromQuery = (input) => {
  let params;
  if (input && typeof input === 'object' && typeof input.get === 'function') params = input;
  else params = new URLSearchParams(String(input ?? '').replace(/^\?/, ''));
  const raw = params.get('konu') ?? params.get('topic');
  return QUERY_TOPICS[foldSlug(raw)] || DEFAULT_TOPIC;
};

export const isValidTopic = (topic) => CONTACT_TOPICS.includes(topic);

// Sunucudaki doğrulamayla uyumlu, sade bir kontrol: yerel kısım@alan.uzantı, boşluk yok, tek @
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

export const isValidEmail = (email) => {
  const v = String(email ?? '').trim();
  return v.length > 0 && v.length <= EMAIL_MAX && EMAIL_RE.test(v);
};

/**
 * İstemci doğrulaması. Dönüş: { alan: hataAnahtarı } (boşsa geçerli). Anahtarlar i18n'de contact.form.errors.<anahtar>.
 * name: nameRequired | nameLength; email: emailInvalid; topic: topicInvalid; message: messageRequired | messageShort | messageLong
 */
export const validateContactForm = ({ name, email, topic, message } = {}) => {
  const errors = {};
  const n = String(name ?? '').trim();
  if (!n) errors.name = 'nameRequired';
  else if (n.length < NAME_MIN || n.length > NAME_MAX) errors.name = 'nameLength';
  if (!isValidEmail(email)) errors.email = 'emailInvalid';
  if (!isValidTopic(topic)) errors.topic = 'topicInvalid';
  const m = String(message ?? '').trim();
  if (!m) errors.message = 'messageRequired';
  else if (m.length < MESSAGE_MIN) errors.message = 'messageShort';
  else if (m.length > MESSAGE_MAX) errors.message = 'messageLong';
  return errors;
};

/** Sunucunun 400 yanıtındaki errors[] (alan anahtarları) → { alan: hataAnahtarı } */
export const serverFieldErrors = (errors) => {
  const map = { name: 'nameLength', email: 'emailInvalid', topic: 'topicInvalid', message: 'messageLength' };
  const out = {};
  (Array.isArray(errors) ? errors : []).forEach((key) => {
    const k = String(key || '').trim().toLowerCase();
    if (map[k]) out[k] = map[k];
  });
  return out;
};

const cleanPath = (value) => {
  let p = String(value ?? '').trim();
  const cut = p.search(/[?#]/);
  if (cut >= 0) p = p.slice(0, cut);
  if (!p.startsWith('/')) return null;
  return p.slice(0, SOURCE_PATH_MAX);
};

/**
 * Kaynak sayfa: aynı siteden gelindiyse yönlendiren sayfanın yolu (ör. "/crm"), yoksa formun yolu ("/contact").
 * Sorgu dizesi ve hash hiçbir zaman gönderilmez (token/kişisel veri taşıyabilir); başka sitelerin adresi gönderilmez.
 */
export const sourcePathFromReferrer = (referrer, origin, pathname) => {
  const fallback = cleanPath(pathname) || '/contact';
  if (!referrer || !origin) return fallback;
  try {
    const ref = new URL(referrer);
    if (ref.origin !== new URL(origin).origin) return fallback;
    return cleanPath(ref.pathname) || fallback;
  } catch {
    return fallback;
  }
};

/** API gövdesi (api/Contact/Send). Metinler kırpılır; e-posta küçük harfe çevrilmez (sunucu normalleştirir). */
export const buildContactPayload = (form = {}, { formOpenedAt, submittedAt, sourcePath } = {}) => ({
  name: String(form.name ?? '').trim(),
  email: String(form.email ?? '').trim(),
  topic: isValidTopic(form.topic) ? form.topic : DEFAULT_TOPIC,
  message: String(form.message ?? '').trim(),
  website: String(form.website ?? ''),
  formOpenedAt: Number.isFinite(formOpenedAt) ? Math.round(formOpenedAt) : null,
  submittedAt: Number.isFinite(submittedAt) ? Math.round(submittedAt) : null,
  sourcePath: cleanPath(sourcePath),
});
